<template>
    <div data-test-view="feedback-request">
        <!-- Request -->
        <div v-show="showRequestForm">
            <div class="row">
                <div class="col-md-4 col-md-offset-4 text-center">
                    <img :src="imgFeedback" alt="Feedback Request" width="150" />
                </div>
            </div>

            <l-space :size="2" />

            <!-- Select Users -->
            <l-input-typeahead
                :label="''"
                :data="users"
                :on-select="typeaheadUserOnSelect"
                :on-input-change="typeaheadUserOnInputChange"
                :placeholder="$t(`feedback.request.placeholderSelectUser`)"
                data-test-id="feedback-request-typeahead"
            />

            <span
                v-for="user in selectedUsers"
                v-show="selectedUsers.length"
                :key="user._id"
                class="r-default-margin-s"
            >
                <span
                    class="label label-default"
                    data-test-class="feedback-request-user"
                    tabindex="0"
                    @click="removeSelectedUser({ user })"
                    @keyup.enter="removeSelectedUser({ user })"
                >
                    {{ user.firstname }} {{ user.lastname }}
                    <i class="fa fa-remove l-default-margin-s"></i>
                </span>
            </span>
            <!--/Select Users -->

            <l-space :size="2" />

            <!-- Message -->
            <l-input-text
                ref="input-message"
                v-model="message"
                type="textarea"
                :label="$t(`feedback.request.labelMessage`)"
                data-test-id="feedback-request-message"
            />
            <!--/Message -->

            <!-- Skills -->
            <div v-show="rights.F_STRUCTURED">
                <button
                    class="btn btn-default btn-raised"
                    data-test-id="feedback-request-show-select-skills"
                    tabindex="0"
                    @click="() => (showSelectSkills = !showSelectSkills)"
                >
                    {{ $t(`feedback.request.btnPickSpecific`) }}
                </button>

                <div v-show="showSelectSkills">
                    <!-- Filter Skills -->
                    <l-space :size="2" />
                    <l-input-text
                        v-model="filterSkills"
                        :label="$t(`feedback.request.labelSearchSkill`)"
                        :optional="true"
                        data-test-id="feedback-request-search-select-skills"
                    />
                    <!--/Filter Skills -->

                    <div v-for="skill in skills" :key="skill._id">
                        <div
                            v-show="!selectedSkills.map(s => s._id).includes(skill._id)"
                            class="hr-right-line"
                            data-test-class="feedback-request-select-skill"
                            tabindex="0"
                            @click="selectSkill({ skill })"
                            @keyup.enter="selectSkill({ skill })"
                        >
                            <i class="fal fa-square color-primary"></i>
                            <span class="l-default-margin-s r-default-margin-s">{{
                                skill.name
                            }}</span>
                            <span
                                v-show="skill.type !== 'goalUser' && skill.description"
                                v-b-popover.hover.top="{
                                    content: skill.description,
                                }"
                                class="color-grey explanation"
                            >
                                <i class="fa fa-info"></i>
                            </span>
                        </div>

                        <div
                            v-show="selectedSkills.map(s => s._id).includes(skill._id)"
                            class="hr-right-line"
                            data-test-class="feedback-request-unselect-skill"
                            tabindex="0"
                            @click="unselectSkill({ skill })"
                            @keyup.enter="unselectSkill({ skill })"
                        >
                            <i class="fal fa-check-square color-primary"></i>
                            <span class="l-default-margin-s r-default-margin-s">{{
                                skill.name
                            }}</span>
                            <span
                                v-show="skill.type !== 'goalUser' && skill.description"
                                v-b-popover.hover.top="{
                                    content: skill.description,
                                }"
                                class="color-grey explanation"
                            >
                                <i class="fa fa-info"></i>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <!--/Skills -->

            <!-- Error -->
            <l-text-alert v-show="error" type="danger" :text="error" />
            <!--/Error -->

            <l-space :size="2" />

            <!-- Footer -->
            <div class="footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    data-test-id="feedback-request-cancel"
                    @click="$emit('close')"
                >
                    {{ $t(`feedback.request.btnCancel`) }}
                </button>

                <button
                    class="btn btn-primary btn-raised"
                    type="button"
                    data-test-id="feedback-request-send"
                    @click="createFeedbackRequest()"
                >
                    {{ $t(`feedback.request.btnSend`) }}
                </button>
            </div>
            <!--/Footer -->
        </div>

        <!--/Request -->

        <!-- Confirmation  -->
        <div v-show="showConfirmation">
            <div class="row">
                <div class="col-xs-12 text-center">
                    <img :src="imgFeedback" alt="Feedback Request Sent" width="150" />
                    <h3>{{ $t(`feedback.request.titleThanks`) }}</h3>
                </div>
            </div>
            <div class="footer">
                <button
                    class="btn btn-primary"
                    type="button"
                    data-test-id="feedback-request-close"
                    @click="$emit('close')"
                >
                    {{ $t(`feedback.request.btnClose`) }}
                </button>
            </div>
        </div>
        <!--/Confirmation -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import format from "@/utils/format-typeahead";

import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LSpace from "@/components/LSpace";
import LTextAlert from "@/components/LTextAlert";

import imgFeedback from "@/img/icons/feedback.png";

export default {
    components: {
        LInputText,
        LInputTypeahead,
        LSpace,
        LTextAlert,
    },
    data() {
        return {
            showRequestForm: true,
            showConfirmation: false,

            selectedUsers: [],
            message: this.$t(`feedback.request.placeholderMessage`),

            showSelectSkills: false,
            filterSkills: "",
            selectedSkills: [],

            error: "",
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            rights: "users/current/rights",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
            getSkills: "skills/getSkills",
            getUserDetails: "users/details/details",
        }),
        users() {
            let list = this.usersSearchList
                // Filter out current user
                .filter(user => user._id !== this.user._id)
                // Filter out selected users
                .filter(user => !this.selectedUsers.map(u => u._id).includes(user._id));

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" })
                    // Filter out selected users
                    .filter(user => !this.selectedUsers.map(u => u._id).includes(user._id));
            }

            return list.map(format.users);
        },
        skills() {
            const skills = this.getSkills();

            if (this.filterSkills) {
                return skills.filter(skill =>
                    skill.name.toLowerCase().includes(this.filterSkills.toLowerCase()),
                );
            }
            return skills;
        },
    },
    created() {
        this.imgFeedback = imgFeedback;

        if (this.$route.query?.to) {
            this.selectedUsers.push(this.getUserDetails(this.$route.query.to));
        }
    },
    methods: {
        typeaheadUserOnSelect(user) {
            this.selectedUsers.push(user);
        },
        typeaheadUserOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        removeSelectedUser({ user }) {
            const index = this.selectedUsers.findIndex(u => u._id === user._id);

            if (index != null) {
                this.selectedUsers.splice(index, 1);
            }
        },
        selectSkill({ skill }) {
            this.selectedSkills.push(skill);
        },
        unselectSkill({ skill }) {
            const index = this.selectedSkills.findIndex(s => s._id === skill._id);

            if (index != null) {
                this.selectedSkills.splice(index, 1);
            }
        },

        createFeedbackRequest() {
            if (!this.selectedUsers.length) {
                this.error = this.$t("feedback.request.errorUser");
                return;
            } else if (!this.message) {
                this.error = this.$t("feedback.request.errorMessage");
                return;
            } else {
                this.error = "";
            }

            this.$store
                .dispatch({
                    type: "feedback/request/send",
                    payload: {
                        receivers: this.selectedUsers,
                        message: this.message,
                        skills: this.selectedSkills.map(skill => skill._id),
                    },
                })
                .then(() => {
                    this.showRequestForm = false;
                    this.showConfirmation = true;
                });
        },
    },
};
</script>

<style scoped></style>
